/* eslint-disable camelcase */
import React, { Ref } from "react";
import BrandMark from "@themuse/design-system/lib/components/Icons/BrandMark";
import CurvedLine from "@themuse/design-system/lib/components/Icons/CurvedLine";
import { TrackingObject } from "@themuse/design-system/lib/types/TrackingTypes";

import { JobQuizNavigationRef } from "./JobQuizNavigation";
import JobQuizQuestion, { QuestionResponse } from "./JobQuizQuestion";
import { JobQuizProcessing } from "./JobQuizReview";

import styles from "./JobQuizContainer.module.scss";

interface QuizQuestion {
    autoNav: boolean;
    isRequired: boolean;
    details?: string;
    field?: string;
    fieldType: string;
    flow?: boolean;
    options: any;
    title: string;
    questionId?: string;
    onChange?: (value: any) => void;
}

interface JobQuizContainerProps {
    nextText?: string;
    prevText?: string;
    quizType: string;
    processingTitle?: string;
    reviewTitle?: string;
    reviewRetakeText?: string;
    reviewViewResultsText?: string;
    questions: QuizQuestion[];
    showPrevious?: boolean;
    submitText?: string;
    skipText?: string;
    trackingOverrides?: TrackingObject;
    clickTrackingOverrides?: TrackingObject;
    onSubmit?: (quizData: any) => void;
}

/**
 * Generates a random string of 6 characters using uppercase letters, lowercase letters, and digits.
 */
const randomString = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    const charactersLength = characters.length;
    for (let i = 0; i < 6; i += 1) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};

/**
 * A Job Quiz container that manages the state of the quiz, question responses, tracking data, and navigation.
 */
const JobQuizContainer = (props: JobQuizContainerProps) => {
    const {
        showPrevious,
        submitText = "submit",
        nextText = "continue",
        prevText = "back",
        skipText = "skip",
        quizType = "quiz",
        questions,
        processingTitle = "Calculating your job matches...",
        reviewTitle = "This way to work that's worth it",
        reviewRetakeText = "Retake Quiz",
        reviewViewResultsText = "View Results",
        trackingOverrides,
        clickTrackingOverrides,
        onSubmit
    } = props;
    const ref: Ref<HTMLDivElement> = React.useRef(null); // Updates tracking props on the container
    const galleryRef: Ref<HTMLDivElement> = React.useRef(null); // Handles the cycling of questions
    const [activeIndex, setActiveIndex] = React.useState(0); // 0 based index representing the current question being shown
    const [responses, setResponses] = React.useState<QuestionResponse[]>([]); // Array containing all question responses
    const [showReview, setShowReview] = React.useState(false); // True when the quiz is 'complete' to show or hide the review screen
    const [impressionId, setImpressionId] = React.useState(""); // Random alphanumeric string to show a unique quiz view
    const [impressionCount, setImpressionCount] = React.useState(1); // # of attempts (updated when 'retake' is clicked)
    const activeQuestion = questions[activeIndex];
    const activeQuestionId = activeQuestion.questionId || `question-${activeIndex + 1}`;

    // useMemo so this is not recalculated on every re-render
    const initialTracking = React.useMemo(() => ({
        sp_quiz_type: quizType || "quiz",
        sp_quiz_id: `${impressionId}-${impressionCount}`,
        sp_quiz_question_name: activeQuestionId,
        sp_quiz_question_order: activeIndex + 1,
        ...(trackingOverrides || {})
    }), [activeIndex, activeQuestionId, impressionCount, impressionId, quizType, trackingOverrides]);

    // useMemo so this is not recalculated on every re-render
    const initialClickTracking = React.useMemo(() => ({
        ...initialTracking,
        sp_click_button_type: "job_quiz_answer",
        sp_click_component: "button",
        sp_click_tile_type: null,
        sp_click_insert_type: "job_quiz",
        sp_click_button_text: "explore open jobs",
        sp_click_position: "second_para",
        sp_click_target_page_section: null,
        ...clickTrackingOverrides,
    }), [clickTrackingOverrides, initialTracking]);

    const [trackingData, setTrackingData] = React.useState<TrackingObject>(initialTracking);
    const [clickTrackingData, setClickTrackingData] = React.useState<TrackingObject>(initialClickTracking);

    // When the quiz loads create the impression Id. This should only ever happen once on load.
    // This is in a useEffect to prevent resetting it on the server and client.
    React.useEffect(() => {
        const impressionStr = randomString();
        setImpressionId(impressionStr);
    }, []);

    // If any tracking data changes set it to the state to pass into the buttons
    React.useEffect(() => {
        setTrackingData({
            ...initialTracking,
            sp_quiz_id: `${impressionId}-${impressionCount}`
        });
        setClickTrackingData({
            ...initialClickTracking,
            sp_quiz_id: `${impressionId}-${impressionCount}`
        });
    }, [
        impressionCount,
        initialClickTracking,
        initialTracking,
        impressionId
    ]);

    const numSteps = questions.length || 0;

    const quizData = {
        quizType: quizType || "quiz",
        quizId: `${impressionId}-${impressionCount}`,
        questions: responses
    };

    // This is to update the question shown (convert the step to 0 based index)
    const handleStepChange = (step: number) => {
        const index: number = step - 1;
        setActiveIndex(index);
    };

    const resetQuiz = () => {
        setActiveIndex(0);
        setShowReview(false);
        setResponses([]);
        const newCount = impressionCount + 1;
        setImpressionCount(newCount);

        if (ref?.current) {
            ref.current.removeAttribute("data-track");
            ref.current.removeAttribute("data-previous-question");
        }
    };

    const submitQuiz = () => {
        if (onSubmit) {
            onSubmit(quizData);
        }
    };

    const completeQuiz = () => {
        if (ref?.current) {
            ref.current.setAttribute("data-track", JSON.stringify(quizData));
        }

        setShowReview(true);
    };

    // Updates the responses state variable (updates or adds the value depending on whether it exists)
    // Also handles the navigation if autonav is true for the question
    const onQuestionResponse = (value: QuestionResponse, skipped: boolean = false) => {
        const newResponses: QuestionResponse[] = Object.assign([], responses);

        const foundResponse = newResponses.findIndex((elem) => elem.questionId === value.questionId);

        if (foundResponse > -1) {
            newResponses[foundResponse] = value;
        } else {
            newResponses.push(value);
        }
        setResponses(newResponses);

        if (activeQuestion.autoNav || skipped) {
            handleStepChange(activeIndex + 2); // Set the step to the current index + 2 (current step is index + 1)
        }
    };

    const handleSkip = () => {
        const questionResponse: QuestionResponse = {
            questionId: activeQuestionId,
            questionOrder: activeIndex + 1,
            field: activeQuestion.field,
            response: null
        };

        onQuestionResponse(questionResponse, true);
    };

    const disableNext = !responses.some((elem) => elem.questionId === activeQuestionId);

    const quizNavProps = {
        currentStep: activeIndex + 1,
        numSteps,
        nextText,
        disableNext,
        prevText,
        showPrevious: showPrevious && !showReview,
        submitText,
        canSkip: !activeQuestion.isRequired,
        showNext: !activeQuestion.autoNav && !showReview,
        onSkip: handleSkip,
        onStepChange: handleStepChange,
        onSubmit: completeQuiz,
        galleryRef,
        skipText
    };

    const renderQuestions = () => (
        questions.map((question: QuizQuestion, index: number) => {
            const questionId = question?.questionId ?? `question-${index + 1}`;
            const viewerClass = (index === activeIndex && !showReview) ? styles.viewer : "";
            const clickTracking: TrackingObject = { ...clickTrackingData };
            clickTracking.sp_quiz_question_name = questions[index].questionId ?? `question-${index + 1}`;
            clickTracking.sp_quiz_question_order = index + 1;

            return (
                <JobQuizQuestion
                    key={`${impressionId}-${impressionCount}-${questionId}`}
                    {...question}
                    trackingData={trackingData}
                    clickTrackingData={clickTracking}
                    extraClass={viewerClass}
                    questionId={questionId}
                    onResponseChange={onQuestionResponse}
                />
            );
        })
    );

    return (
        <div
            ref={ref}
            className={styles.container}
            data-tracking-event-type="impression"
            data-tracking-props={JSON.stringify(trackingData)}
        >
            <div
                ref={galleryRef}
                className={styles.gallery}
            >
                { renderQuestions() }

                <JobQuizProcessing
                    processingTitle={processingTitle}
                    reviewTitle={reviewTitle}
                    reviewRetakeText={reviewRetakeText}
                    reviewViewResultsText={reviewViewResultsText}
                    showProcessing={showReview}
                    onViewResults={() => { submitQuiz(); }}
                    onReset={resetQuiz}
                    clickTrackingData={clickTrackingData}
                />
            </div>

            <JobQuizNavigationRef
                {...quizNavProps}
                clickTrackingData={clickTrackingData}
            />
            <CurvedLine className={styles.curvedLine} />
            <BrandMark className={styles.brandMark} />
        </div>
    );
};
JobQuizContainer.displayName = "JobQuizContainer";

export { JobQuizContainer as default };
export type { JobQuizContainerProps };
